import React, { FC, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { AnyAction } from 'redux'
import {
    Drawer,
    Form,
    Button,
    Col,
    Row,
    Input,
    Space,
    Divider,
    Switch,
    Tooltip,
    message
} from 'antd'
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import PrefixService from "../../../services/prefix"
import { requestAuthInit } from '../../../store/auth/auth.actions'
import { BankConfig } from '../../../@types/prefix'
import { ThunkDispatch } from '../../../@types/store'
import { bankSelect, bankApp } from '../../../utils/bank'

interface EditBankProps {
    close: () => void
    isOpen: boolean

    data: any
}

const EditBank: FC<EditBankProps> = ({ close, isOpen, data }) => {
    const [form] = Form.useForm();
    const title = "แก้ไขบัญชีธนาคาร"
    const [btnLoading, setBtnLoading] = useState<boolean>(false)
    const [btnClearLoading, setBtnClearLoading] = useState<boolean>(false)
    const [btnApiEndpointLoading, setBtnApiEndpointLoading] = useState<boolean>(false)
    const dispatch: ThunkDispatch<AnyAction> = useDispatch()
    const [nameBankApp, setNameBankApp] = useState<string>("")

    const [smsAPIEndpoint, setSmsAPIEndpoint] = useState<string>("")


    const updateData = async (values: BankConfig) => {
        try {
            const res = await PrefixService.editBankAccount(values)
            setBtnLoading(false)
            dispatch(requestAuthInit(false))
            toast.success(res.message)
            close()
        } catch ({ message }) {
            toast.error(message as string)
            setBtnLoading(false)
        }
    }

    const handleFormSubmit = () => {
        form.validateFields()
            .then((values: BankConfig) => {
                setBtnLoading(true)
                values.bank = data.bank
                values.oldAccountNumber = data.accountNumber
                values.warning = data.warning
                if (values.meta.password_new !== undefined) {
                    values.meta.password = values.meta.password_new
                }
                delete values.meta.password_new
                updateData(values)
            })
            .catch((errorInfo) => { });
    };

    const handleCopy = (values: string) => {
        navigator.clipboard.writeText(values)
        message.success('คัดลอกข้อมูลสำเร็จ');
    }

    const handleReGenAPIEndpoint = () => {
        setBtnApiEndpointLoading(true)
        Swal.fire({
            title: 'แจ้งเตือน!',
            text: `คุณต้องการสร้าง SMS API ENDPOINT ใหม่ใช่หรือไม่`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            cancelButtonText: 'ยกเลิก',
            confirmButtonText: 'ตกลง'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setBtnApiEndpointLoading(false)
                    const res = await PrefixService.regenSMSAccount(data)
                    dispatch(requestAuthInit(false))
                    toast.success(res.message)
                    setSmsAPIEndpoint(res.endpoint ? res.endpoint : "")
                } catch ({ message }) {
                    toast.error(message as string)
                    setBtnApiEndpointLoading(false)
                }
            } else {
                setBtnApiEndpointLoading(false)
            }
        })
    }

    const handleClear = async () => {
        Swal.fire({
            title: 'แจ้งเตือน!',
            text: `คุณต้องการล้างการตั้งค่า ${nameBankApp} นี้ใช่หรือไม่`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            cancelButtonText: 'ยกเลิก',
            confirmButtonText: 'ตกลง'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    data.meta.username = ""
                    data.meta.password = ""
                    data.oldAccountNumber = data.accountNumber
                    const res = await PrefixService.editBankAccount(data)
                    setBtnClearLoading(false)
                    dispatch(requestAuthInit(false))
                    toast.success(res.message)
                    form.setFieldsValue(data)
                    form.setFieldsValue({ "bank": bankSelect[data.bank] })
                    setNameBankApp(bankApp[data.bank])
                } catch ({ message }) {
                    toast.error(message as string)
                    setBtnClearLoading(false)
                }
            }
        })
    }

    useEffect(() => {
        if (form && isOpen && data) {
            form.resetFields();
            form.setFieldsValue(data)
            form.setFieldsValue({ "bank": bankSelect[data.bank] })
            setNameBankApp(bankApp[data.bank])
            setSmsAPIEndpoint(data.apiURL)
        }
    }, [form, isOpen, data]);

    return (
        <>
            <Drawer
                title={title}
                onClose={close}
                visible={isOpen}
                bodyStyle={{ paddingBottom: 80 }}
                width={global.window.innerWidth <= 1200 ? global.window.innerWidth : global.window.innerWidth * .4}
                extra={
                    <Space>
                        <Button
                            onClick={handleFormSubmit}
                            type="primary"
                            loading={btnLoading}
                            size="large">
                            <i className="fa-solid fa-save me-2"></i>
                            บันทึก
                        </Button>
                    </Space>
                }
            >
                <Form
                    form={form}
                    name="EditBank"
                    layout="vertical"
                    hideRequiredMark>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="enable"
                                label="สถานะ"
                                valuePropName="checked">
                                <Switch
                                    checkedChildren="เปิด"
                                    unCheckedChildren="ปิด" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="hide"
                                label="ซ่อนจากเว็บไซต์"
                                valuePropName="checked">
                                <Switch
                                    checkedChildren="เปิด"
                                    unCheckedChildren="ปิด" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="accountName"
                                label="ชื่อบัญชี"
                                rules={[{ required: true, message: 'กรุณากรอกชื่อบัญชี' }]}
                                hasFeedback>
                                <Input placeholder="ชื่อบัญชี" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="accountNumber"
                                label="เลขที่บัญชี"
                                rules={[{ required: true, message: 'กรุณากรอกเลขที่บัญชี' }]}
                                hasFeedback>
                                <Input placeholder="เลขที่บัญชี" />
                            </Form.Item>
                        </Col>
                    </Row>
                    {data?.bank !== "truewallet" &&
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="bank"
                                    label="ธนาคาร">
                                    <Input placeholder="ธนาคาร" disabled />
                                </Form.Item>
                            </Col>
                        </Row>
                    }
                    {/* {data?.apiURL &&  } */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="apiURL"
                                label="SMS API ENDPOINT">
                                <Input.Group className="d-flex" compact>
                                    <Tooltip title="สร้าง SMS API ENDPOINT ใหม่">
                                        <Button icon={<ReloadOutlined />} loading={btnApiEndpointLoading} onClick={() => handleReGenAPIEndpoint()} />
                                    </Tooltip>
                                    <Input
                                        style={{ width: 'calc(100% - 200px)' }}
                                        placeholder="API ENDPOINT"
                                        disabled
                                        value={smsAPIEndpoint} />
                                    <Tooltip title="คัดลอก SMS API ENDPOINT">
                                        <Button icon={<CopyOutlined />} disabled={data?.apiURL ? false : true} onClick={() => handleCopy(smsAPIEndpoint)} />
                                    </Tooltip>
                                </Input.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    {data?.bank && data?.bank !== "truewallet" &&
                        <>
                            <Divider orientation="left">ตั้งค่า {nameBankApp}</Divider>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name={['meta', 'username']}
                                        label="ชื่อผู้ใช้งาน"
                                        rules={[{ required: false, message: "กรุณากรอกชื่อผู้ใช้งาน" }]}
                                        hasFeedback>
                                        <Input placeholder="ชื่อผู้ใช้งาน" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name={['meta', 'password_new']}
                                        label="รหัสผ่าน"
                                        rules={[{ required: false, message: "กรุณากรอกรหัสผ่าน" }]}
                                        hasFeedback>
                                        <Input placeholder="รหัสผ่าน" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Button type="primary" size="large" disabled={btnClearLoading} onClick={handleClear}>ล้างการตั้งค่า {nameBankApp}</Button>
                                </Col>
                            </Row>
                        </>
                    }
                    <Divider />
                    <div className="drawer-actions">
                        <Space>
                            <Button type="primary" htmlType="submit" disabled={btnLoading} onClick={handleFormSubmit} size="large"><i className="fa-solid fa-save me-2"></i> บันทึก</Button>
                            <Button onClick={close} size="large">ยกเลิก</Button>
                        </Space>
                    </div>
                </Form>
            </Drawer>
        </>
    )
}
export default EditBank

